// ============================================================
// LAPORAN PAGE
// ============================================================
import React, { useState, useEffect } from 'react';
import { api, formatDateTime, getInitials } from '../api';

export function LaporanPage({ user }) {
  const [laporan, setLaporan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [komentar, setKomentar] = useState([]);
  const [newKomen, setNewKomen] = useState('');
  const [form, setForm] = useState({ judul: '', isi: '', kategori: 'umum' });
  const [fotos, setFotos] = useState([]);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [saving, setSaving] = useState(false);
  const [tanggal, setTanggal] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => { fetchLaporan(); }, [tanggal]);
  useEffect(() => { if (selected) fetchKomentar(selected.id); }, [selected]);

  const fetchLaporan = async () => {
    setLoading(true);
    try { setLaporan(await api.get(`/laporan?tanggal=${tanggal}`)); }
    catch (err) { showMsg(err.message, 'error'); }
    finally { setLoading(false); }
  };

  const fetchKomentar = async (id) => {
    try { setKomentar(await api.get(`/komentar/laporan/${id}`)); }
    catch {}
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3000);
  };

  const submitLaporan = async () => {
    if (!form.judul || !form.isi) { showMsg('Judul dan isi wajib diisi', 'error'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fotos.forEach(f => fd.append('foto', f));
      await api.postForm('/laporan', fd);
      showMsg('Laporan berhasil dikirim');
      setShowForm(false);
      setForm({ judul: '', isi: '', kategori: 'umum' });
      setFotos([]);
      fetchLaporan();
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const sendKomen = async () => {
    if (!newKomen.trim()) return;
    try {
      await api.post(`/komentar/laporan/${selected.id}`, { isi: newKomen });
      setNewKomen('');
      fetchKomentar(selected.id);
    } catch {}
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/laporan/${id}/status`, { status });
      showMsg('Status diupdate');
      fetchLaporan();
      setSelected(null);
    } catch (err) { showMsg(err.message, 'error'); }
  };

  const KATEGORI = ['umum', 'insiden', 'kerusakan', 'kehilangan', 'lainnya'];
  const STATUS_COLOR = { terkirim: 'badge-gold', ditinjau: 'badge-yellow', selesai: 'badge-green', draft: 'badge-gray' };

  return (
    <div className="page fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 className="page-title">Laporan</h1>
          <p className="page-subtitle">Laporan kejadian & insiden</p>
        </div>
        <button className="btn btn-gold btn-sm" onClick={() => setShowForm(true)}>+ Buat</button>
      </div>

      {msg.text && <div className={`toast toast-${msg.type}`}>{msg.text}</div>}

      {/* Filter Tanggal */}
      <div style={{ marginBottom: 20 }}>
        <input type="date" className="input" value={tanggal} onChange={e => setTanggal(e.target.value)} />
      </div>

      {loading ? <div className="loading-center"><div className="loading-spinner" /></div> :
        laporan.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <p>Belum ada laporan pada tanggal ini</p>
          </div>
        ) : laporan.map(l => (
          <div key={l.id} className="list-item" onClick={() => setSelected(l)}>
            <div className="list-item-avatar">{getInitials(l.pembuat_name)}</div>
            <div className="list-item-content">
              <div className="list-item-title">{l.judul}</div>
              <div className="list-item-sub">{l.pembuat_name} · {l.wilayah_nama}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{formatDateTime(l.created_at)}</div>
            </div>
            <div className="list-item-right">
              <span className={`badge ${STATUS_COLOR[l.status] || 'badge-gray'}`}>{l.status}</span>
              <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>{l.kategori}</span>
            </div>
          </div>
        ))
      }

      {/* Detail Modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal-sheet" style={{ maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <h2 className="modal-title" style={{ marginBottom: 0, flex: 1, paddingRight: 12 }}>{selected.judul}</h2>
              <span className={`badge ${STATUS_COLOR[selected.status] || 'badge-gray'}`}>{selected.status}</span>
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 16 }}>
              {selected.pembuat_name} · {selected.wilayah_nama} · {formatDateTime(selected.created_at)}
            </div>
            <p style={{ fontSize: '0.9rem', lineHeight: 1.7, marginBottom: 16, color: 'var(--text-primary)' }}>{selected.isi}</p>

            {/* Foto */}
            {selected.foto_urls && JSON.parse(selected.foto_urls || '[]').length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {JSON.parse(selected.foto_urls).map((url, i) => (
                  <img key={i} src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                ))}
              </div>
            )}

            {/* Status Update */}
            {(user.role === 'admin' || user.role === 'spv') && selected.status !== 'selesai' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {selected.status === 'terkirim' && (
                  <button className="btn btn-outline btn-sm btn-full" onClick={() => updateStatus(selected.id, 'ditinjau')}>Tinjau</button>
                )}
                <button className="btn btn-gold btn-sm btn-full" onClick={() => updateStatus(selected.id, 'selesai')}>Selesaikan</button>
              </div>
            )}

            <div className="divider" />
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>KOMENTAR ({komentar.length})</div>
            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 12 }}>
              {komentar.map((k, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div className="list-item-avatar" style={{ width: 28, height: 28, fontSize: '0.7rem' }}>{getInitials(k.user_name)}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600 }}>{k.user_name} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {formatDateTime(k.created_at)}</span></div>
                      <div style={{ fontSize: '0.85rem', marginTop: 4 }}>{k.isi}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="input" style={{ flex: 1 }} placeholder="Tulis komentar..." value={newKomen} onChange={e => setNewKomen(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendKomen()} />
              <button className="btn btn-gold" onClick={sendKomen}>Kirim</button>
            </div>
          </div>
        </div>
      )}

      {/* Form Buat Laporan */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 className="modal-title">Buat Laporan</h2>
            <div className="input-group">
              <label className="input-label">Judul</label>
              <input className="input" placeholder="Judul laporan..." value={form.judul} onChange={e => setForm(f => ({ ...f, judul: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Kategori</label>
              <select className="input" value={form.kategori} onChange={e => setForm(f => ({ ...f, kategori: e.target.value }))}>
                {KATEGORI.map(k => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Isi Laporan</label>
              <textarea className="input" rows={4} placeholder="Deskripsikan kejadian secara detail..." value={form.isi} onChange={e => setForm(f => ({ ...f, isi: e.target.value }))} style={{ resize: 'none' }} />
            </div>
            <div className="input-group">
              <label className="input-label">Foto (opsional, maks 5)</label>
              <input type="file" accept="image/*" multiple className="input" onChange={e => setFotos(Array.from(e.target.files).slice(0, 5))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost btn-full" onClick={() => setShowForm(false)}>Batal</button>
              <button className="btn btn-gold btn-full" onClick={submitLaporan} disabled={saving}>{saving ? '...' : 'Kirim Laporan'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// REQUEST PAGE
// ============================================================
export function RequestPage({ user }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [komentar, setKomentar] = useState([]);
  const [newKomen, setNewKomen] = useState('');
  const [anggotaList, setAnggotaList] = useState([]);
  const [form, setForm] = useState({ judul: '', isi: '', prioritas: 'medium' });
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [wilayahList, setWilayahList] = useState([]);
  const [filterWilayah, setFilterWilayah] = useState('');

  useEffect(() => {
    if (user.role !== 'client') {
      api.get('/wilayah').then(setWilayahList).catch(() => {});
    }
  }, []);

  useEffect(() => { fetchAll(); }, [filter, filterWilayah]);
  useEffect(() => { if (selected) fetchKomentar(selected.id); }, [selected]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.append('status', filter);
      if (filterWilayah) params.append('wilayah_id', filterWilayah);
      const paramStr = params.toString() ? `?${params.toString()}` : '';
      const [r, a] = await Promise.all([
        api.get(`/request${paramStr}`),
        user.role !== 'client' ? api.get('/users') : Promise.resolve([]),
      ]);
      setRequests(r);
      setAnggotaList(a.filter ? a.filter(u => u.role === 'anggota' || u.role === 'spv') : []);
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setLoading(false); }
  };

  const fetchKomentar = async (id) => {
    try { setKomentar(await api.get(`/komentar/request/${id}`)); } catch {}
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3000);
  };

  const submitRequest = async () => {
    if (!form.judul || !form.isi) { showMsg('Judul dan isi wajib diisi', 'error'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.postForm('/request', fd);
      showMsg('Request berhasil dikirim');
      setShowForm(false);
      setForm({ judul: '', isi: '', prioritas: 'medium' });
      fetchAll();
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const updateRequest = async (id, updates) => {
    try {
      await api.put(`/request/${id}`, updates);
      showMsg('Request diupdate');
      fetchAll();
      setSelected(null);
    } catch (err) { showMsg(err.message, 'error'); }
  };

  const sendKomen = async () => {
    if (!newKomen.trim()) return;
    try {
      await api.post(`/komentar/request/${selected.id}`, { isi: newKomen });
      setNewKomen('');
      fetchKomentar(selected.id);
    } catch {}
  };

  const PRIORITY_CLASS = { urgent: 'priority-urgent', high: 'priority-high', medium: 'priority-medium', low: 'priority-low' };
  const STATUS_COLOR = { baru: 'badge-red', diproses: 'badge-yellow', selesai: 'badge-green', ditolak: 'badge-gray' };

  return (
    <div className="page fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 className="page-title">Request</h1>
          <p className="page-subtitle">{user.role === 'client' ? 'Request & keluhan ke tim security' : 'Request dari client'}</p>
        </div>
        {user.role === 'client' && (
          <button className="btn btn-gold btn-sm" onClick={() => setShowForm(true)}>+ Request</button>
        )}
      </div>

      {msg.text && <div className={`toast toast-${msg.type}`}>{msg.text}</div>}

      {/* Filter Status */}
      <div className="chip-group" style={{ marginBottom: 12 }}>
        {['', 'baru', 'diproses', 'selesai', 'ditolak'].map(s => (
          <button key={s} className={`chip ${filter === s ? 'active' : ''}`} onClick={() => setFilter(s)}>
            {s === '' ? 'Semua' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
      {/* Filter Wilayah (admin/spv) */}
      {user.role !== 'client' && wilayahList.length > 0 && (
        <select className="input" style={{ marginBottom: 16, fontSize: '0.85rem' }}
          value={filterWilayah} onChange={e => setFilterWilayah(e.target.value)}>
          <option value="">Semua Wilayah</option>
          {wilayahList.map(w => <option key={w.id} value={w.id}>{w.nama}</option>)}
        </select>
      )}

      {loading ? <div className="loading-center"><div className="loading-spinner" /></div> :
        requests.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📬</div>
            <p>Belum ada request</p>
          </div>
        ) : requests.map(r => (
          <div key={r.id} className="list-item" onClick={() => setSelected(r)}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--navy-600)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
              {r.prioritas === 'urgent' ? '🚨' : r.prioritas === 'high' ? '⚠️' : r.prioritas === 'medium' ? '📋' : '📌'}
            </div>
            <div className="list-item-content">
              <div className="list-item-title">{r.judul}</div>
              <div className="list-item-sub">{r.client_name} · {r.wilayah_nama}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{formatDateTime(r.created_at)}</div>
            </div>
            <div className="list-item-right">
              <span className={`badge ${STATUS_COLOR[r.status] || 'badge-gray'}`}>{r.status}</span>
              <span className={`badge ${PRIORITY_CLASS[r.prioritas]}`}>{r.prioritas}</span>
            </div>
          </div>
        ))
      }

      {/* Detail Modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal-sheet" style={{ maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <h2 className="modal-title" style={{ marginBottom: 0, flex: 1, paddingRight: 12 }}>{selected.judul}</h2>
              <span className={`badge ${STATUS_COLOR[selected.status] || 'badge-gray'}`}>{selected.status}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              <span className={`badge ${PRIORITY_CLASS[selected.prioritas]}`}>{selected.prioritas}</span>
              <span className="badge badge-gray">{selected.wilayah_nama}</span>
            </div>
            <p style={{ fontSize: '0.9rem', lineHeight: 1.7, marginBottom: 16, color: 'var(--text-primary)' }}>{selected.isi}</p>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 16 }}>
              Dari: {selected.client_name} · {formatDateTime(selected.created_at)}
              {selected.assigned_name && <span> · Ditugaskan ke: <strong>{selected.assigned_name}</strong></span>}
            </div>

            {/* Update Status & Assign (admin/spv/anggota) */}
            {user.role !== 'client' && selected.status !== 'selesai' && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  {selected.status === 'baru' && (
                    <button className="btn btn-outline btn-sm btn-full" onClick={() => updateRequest(selected.id, { status: 'diproses' })}>Mulai Proses</button>
                  )}
                  <button className="btn btn-gold btn-sm btn-full" onClick={() => updateRequest(selected.id, { status: 'selesai' })}>Selesaikan</button>
                  <button className="btn btn-danger btn-sm" onClick={() => updateRequest(selected.id, { status: 'ditolak' })}>Tolak</button>
                </div>
                {(user.role === 'admin' || user.role === 'spv') && (
                  <select className="input" onChange={e => e.target.value && updateRequest(selected.id, { assigned_to: e.target.value })} defaultValue="">
                    <option value="">Tugaskan ke...</option>
                    {anggotaList.map(a => <option key={a.id} value={a.id}>{a.name} ({a.role})</option>)}
                  </select>
                )}
              </div>
            )}

            <div className="divider" />
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>KOMENTAR ({komentar.length})</div>
            <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 12 }}>
              {komentar.map((k, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div className="list-item-avatar" style={{ width: 28, height: 28, fontSize: '0.7rem' }}>{getInitials(k.user_name)}</div>
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{k.user_name} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {formatDateTime(k.created_at)}</span></div>
                      <div style={{ fontSize: '0.85rem', marginTop: 2 }}>{k.isi}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="input" style={{ flex: 1 }} placeholder="Tulis komentar..." value={newKomen} onChange={e => setNewKomen(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendKomen()} />
              <button className="btn btn-gold" onClick={sendKomen}>Kirim</button>
            </div>
          </div>
        </div>
      )}

      {/* Form Buat Request (client) */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 className="modal-title">Buat Request</h2>
            <div className="input-group">
              <label className="input-label">Judul</label>
              <input className="input" placeholder="Judul request..." value={form.judul} onChange={e => setForm(f => ({ ...f, judul: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Prioritas</label>
              <select className="input" value={form.prioritas} onChange={e => setForm(f => ({ ...f, prioritas: e.target.value }))}>
                <option value="low">Rendah</option>
                <option value="medium">Sedang</option>
                <option value="high">Tinggi</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Deskripsi</label>
              <textarea className="input" rows={4} placeholder="Jelaskan request atau keluhan..." value={form.isi} onChange={e => setForm(f => ({ ...f, isi: e.target.value }))} style={{ resize: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost btn-full" onClick={() => setShowForm(false)}>Batal</button>
              <button className="btn btn-gold btn-full" onClick={submitRequest} disabled={saving}>{saving ? '...' : 'Kirim'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// NOTIF PAGE
// ============================================================
export function NotifPage({ user }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchNotif(); }, []);

  const fetchNotif = async () => {
    setLoading(true);
    try { setNotifs(await api.get('/notifikasi')); }
    catch {} finally { setLoading(false); }
  };

  const readAll = async () => {
    try {
      await api.put('/notifikasi/read-all', {});
      fetchNotif();
    } catch {}
  };

  const TYPE_ICON = { absen: '📍', laporan: '📋', request: '🔔', jadwal: '📅', komentar: '💬', sistem: '⚙️' };

  return (
    <div className="page fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="page-title">Notifikasi</h1>
        <button className="btn btn-ghost btn-sm" onClick={readAll}>Tandai Dibaca</button>
      </div>

      {loading ? <div className="loading-center"><div className="loading-spinner" /></div> :
        notifs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔔</div>
            <p>Belum ada notifikasi</p>
          </div>
        ) : notifs.map(n => (
          <div key={n.id} className="list-item" style={{ cursor: 'default', background: n.is_read ? 'var(--navy-700)' : 'var(--navy-600)', borderColor: n.is_read ? 'var(--border)' : 'var(--border-hover)' }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--navy-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
              {TYPE_ICON[n.type] || '📢'}
            </div>
            <div className="list-item-content">
              <div className="list-item-title">{n.judul}</div>
              {n.isi && <div className="list-item-sub">{n.isi}</div>}
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{formatDateTime(n.created_at)}</div>
            </div>
            {!n.is_read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--gold-400)', flexShrink: 0 }} />}
          </div>
        ))
      }
    </div>
  );
}

// ============================================================
// PROFIL PAGE
// ============================================================
export function ProfilPage({ user, setUser }) {
  const [form, setForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [saving, setSaving] = useState(false);
  const { logout, getRoleLabel } = require('../api');

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3000);
  };

  const changePassword = async () => {
    if (!form.old_password || !form.new_password) { showMsg('Semua field wajib diisi', 'error'); return; }
    if (form.new_password !== form.confirm) { showMsg('Password baru tidak cocok', 'error'); return; }
    if (form.new_password.length < 6) { showMsg('Password minimal 6 karakter', 'error'); return; }
    setSaving(true);
    try {
      await api.put('/auth/change-password', { old_password: form.old_password, new_password: form.new_password });
      showMsg('Password berhasil diubah');
      setForm({ old_password: '', new_password: '', confirm: '' });
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="page fade-in">
      <h1 className="page-title">Profil</h1>
      <p className="page-subtitle">Informasi akun kamu</p>

      {msg.text && <div className={`toast toast-${msg.type}`}>{msg.text}</div>}

      {/* User Card */}
      <div className="card" style={{ marginBottom: 20, textAlign: 'center' }}>
        <div style={{ width: 72, height: 72, borderRadius: 'var(--radius-lg)', background: 'linear-gradient(135deg, var(--navy-500), var(--gold-glow))', border: '2px solid var(--border-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', fontWeight: 800, color: 'var(--gold-400)', margin: '0 auto 16px' }}>
          {user?.name?.charAt(0).toUpperCase()}
        </div>
        <h2 style={{ marginBottom: 4 }}>{user?.name}</h2>
        <p style={{ fontSize: '0.85rem', marginBottom: 8 }}>{user?.email}</p>
        <span className="badge badge-gold">{getRoleLabel(user?.role)}</span>
      </div>

      {/* Ganti Password */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16 }}>🔐 Ganti Password</h3>
        <div className="input-group">
          <label className="input-label">Password Lama</label>
          <input type="password" className="input" placeholder="••••••••" value={form.old_password} onChange={e => setForm(f => ({ ...f, old_password: e.target.value }))} />
        </div>
        <div className="input-group">
          <label className="input-label">Password Baru</label>
          <input type="password" className="input" placeholder="••••••••" value={form.new_password} onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))} />
        </div>
        <div className="input-group">
          <label className="input-label">Konfirmasi Password Baru</label>
          <input type="password" className="input" placeholder="••••••••" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
        </div>
        <button className="btn btn-gold btn-full" onClick={changePassword} disabled={saving}>
          {saving ? '...' : 'Simpan Password'}
        </button>
      </div>

      {/* Logout */}
      <button className="btn btn-danger btn-full btn-lg" onClick={logout}>
        🚪 Keluar
      </button>
    </div>
  );
}

// ============================================================
// ADMIN PAGE
// ============================================================
export function AdminPage({ user, defaultTab }) {
  const [tab, setTab] = useState(defaultTab || 'users');
  const [users, setUsers] = useState([]);
  const [wilayah, setWilayah] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showWilayahForm, setShowWilayahForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterRole, setFilterRole] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'anggota', phone: '', wilayah_id: '' });
  const [wForm, setWForm] = useState({ nama: '', alamat: '', keterangan: '', lat: '', lng: '', radius_meter: 200 });
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, [tab]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, w] = await Promise.all([api.get('/users'), api.get('/wilayah')]);
      setUsers(u);
      setWilayah(w);
    } catch {} finally { setLoading(false); }
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3000);
  };

  const saveUser = async () => {
    if (!form.name || !form.email || !form.password) { showMsg('Nama, email, password wajib', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/users', form);
      showMsg('User berhasil ditambahkan');
      setShowForm(false);
      setForm({ name: '', email: '', password: '', role: 'anggota', phone: '', wilayah_id: '' });
      fetchAll();
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const saveWilayah = async () => {
    if (!wForm.nama) { showMsg('Nama wilayah wajib diisi', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/wilayah', wForm);
      showMsg('Wilayah berhasil ditambahkan');
      setShowWilayahForm(false);
      setWForm({ nama: '', alamat: '', keterangan: '', lat: '', lng: '', radius_meter: 200 });
      fetchAll();
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const deactivateUser = async (id) => {
    if (!window.confirm('Nonaktifkan user ini?')) return;
    try { await api.delete(`/users/${id}`); showMsg('User dinonaktifkan'); fetchAll(); }
    catch (err) { showMsg(err.message, 'error'); }
  };

  const ROLE_BADGE = { admin: 'badge-red', spv: 'badge-yellow', anggota: 'badge-green', client: 'badge-blue' };
  const { getRoleLabel } = require('../api');

  return (
    <div className="page fade-in">
      <h1 className="page-title">Kelola Sistem</h1>
      <p className="page-subtitle">Manajemen user dan wilayah</p>

      {msg.text && <div className={`toast toast-${msg.type}`}>{msg.text}</div>}

      {/* Tab */}
      <div className="chip-group" style={{ marginBottom: 20 }}>
        <button className={`chip ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>👥 User</button>
        <button className={`chip ${tab === 'wilayah' ? 'active' : ''}`} onClick={() => setTab('wilayah')}>🗺️ Wilayah</button>
      </div>

      {/* Users Tab */}
      {tab === 'users' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div className="chip-group" style={{ flex: 1 }}>
              {[['', 'Semua'], ['admin', 'Admin'], ['spv', 'SPV'], ['anggota', 'Anggota'], ['client', 'Client']].map(([val, label]) => (
                <button key={val} className={`chip ${filterRole === val ? 'active' : ''}`} onClick={() => setFilterRole(val)}>{label}</button>
              ))}
            </div>
            <button className="btn btn-gold btn-sm" style={{ marginLeft: 8, flexShrink: 0 }} onClick={() => setShowForm(true)}>+ Tambah</button>
          </div>
          {loading ? <div className="loading-center"><div className="loading-spinner" /></div> :
            users.filter(u => filterRole === '' || u.role === filterRole).map(u => (
              <div key={u.id} className="list-item" style={{ cursor: 'pointer' }} onClick={() => setSelectedUser(u)}>
                <div className="list-item-avatar">
                  {u.foto_url
                    ? <img src={u.foto_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                    : u.name?.charAt(0)}
                </div>
                <div className="list-item-content">
                  <div className="list-item-title">{u.name}</div>
                  <div className="list-item-sub">{u.email}</div>
                  {u.wilayah_nama && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>📍 {u.wilayah_nama}</div>}
                </div>
                <div className="list-item-right">
                  <span className={`badge ${ROLE_BADGE[u.role]}`}>{getRoleLabel(u.role)}</span>
                  {u.id !== user.id && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-danger btn-sm" style={{ fontSize: '0.7rem', padding: '3px 8px' }} onClick={() => deactivateUser(u.id)}>Nonaktif</button>
                      <button className="btn btn-sm" style={{ fontSize: '0.7rem', padding: '3px 8px', background: '#450a0a', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 6, cursor: 'pointer' }}
                        onClick={async () => {
                          if (!window.confirm(`Hapus permanen user "${u.name}"? Tindakan ini tidak bisa dibatalkan.`)) return;
                          try { await api.delete(`/users/${u.id}`); showMsg('User dihapus'); setSelectedUser(null); fetchAll(); }
                          catch (err) { showMsg(err.message, 'error'); }
                        }}>Hapus</button>
                    </div>
                  )}
                </div>
              </div>
            ))
          }
        </>
      )}

      {/* Wilayah Tab */}
      {tab === 'wilayah' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-gold btn-sm" onClick={() => setShowWilayahForm(true)}>+ Tambah Wilayah</button>
          </div>
          {loading ? <div className="loading-center"><div className="loading-spinner" /></div> :
            wilayah.map(w => (
              <div key={w.id} className="list-item" style={{ cursor: 'default' }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--gold-glow)', border: '1px solid var(--border-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>🏢</div>
                <div className="list-item-content">
                  <div className="list-item-title">{w.nama}</div>
                  {w.alamat && <div className="list-item-sub">📍 {w.alamat}</div>}
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{w.total_anggota || 0} anggota{w.radius_meter ? ` · radius ${w.radius_meter}m` : ''}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  <span className="badge badge-green">Aktif</span>
                  <button className="btn btn-danger btn-sm" style={{ fontSize: '0.7rem', padding: '3px 8px' }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm('Hapus wilayah ini? Data terkait akan terpengaruh.')) return;
                      try { await api.delete(`/wilayah/${w.id}`); showMsg('Wilayah dihapus'); fetchAll(); }
                      catch (err) { showMsg(err.message, 'error'); }
                    }}>Hapus</button>
                </div>
              </div>
            ))
          }
        </>
      )}

      {/* Modal Tambah User */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 className="modal-title">Tambah User</h2>
            <div className="input-group">
              <label className="input-label">Nama Lengkap</label>
              <input className="input" placeholder="Nama lengkap..." value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Email</label>
              <input type="email" className="input" placeholder="email@domain.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="grid-2">
              <div className="input-group">
                <label className="input-label">Password</label>
                <input type="password" className="input" placeholder="Min 6 karakter" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
              <div className="input-group">
                <label className="input-label">No. HP (WA)</label>
                <input className="input" placeholder="628xxx" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
            </div>
            <div className="grid-2">
              <div className="input-group">
                <label className="input-label">Role</label>
                <select className="input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="spv">Supervisor</option>
                  <option value="anggota">Anggota</option>
                  <option value="client">Client</option>
                </select>
              </div>
              <div className="input-group">
                <label className="input-label">Wilayah</label>
                <select className="input" value={form.wilayah_id} onChange={e => setForm(f => ({ ...f, wilayah_id: e.target.value }))}>
                  <option value="">Pilih wilayah...</option>
                  {wilayah.map(w => <option key={w.id} value={w.id}>{w.nama}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost btn-full" onClick={() => setShowForm(false)}>Batal</button>
              <button className="btn btn-gold btn-full" onClick={saveUser} disabled={saving}>{saving ? '...' : 'Simpan'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Tambah Wilayah */}
      {showWilayahForm && (
        <div className="modal-overlay" onClick={() => setShowWilayahForm(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 className="modal-title">Tambah Wilayah</h2>
            <div className="input-group">
              <label className="input-label">Nama Wilayah</label>
              <input className="input" placeholder="Nama wilayah..." value={wForm.nama} onChange={e => setWForm(f => ({ ...f, nama: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Alamat</label>
              <input className="input" placeholder="Alamat lengkap..." value={wForm.alamat} onChange={e => setWForm(f => ({ ...f, alamat: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Keterangan (opsional)</label>
              <textarea className="input" rows={2} placeholder="Keterangan tambahan..." value={wForm.keterangan} onChange={e => setWForm(f => ({ ...f, keterangan: e.target.value }))} style={{ resize: 'none' }} />
            </div>
            <div className="input-group">
              <label className="input-label">Radius Absensi (meter)</label>
              <input type="number" className="input" placeholder="Contoh: 100" min="10" max="10000"
                value={wForm.radius_meter} onChange={e => setWForm(f => ({ ...f, radius_meter: e.target.value }))} />
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Jarak maksimal anggota dari titik wilayah saat absen masuk
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">Titik Koordinat GPS</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="input" placeholder="Latitude (-6.xxxxx)" value={wForm.lat}
                  onChange={e => setWForm(f => ({ ...f, lat: e.target.value }))} />
                <input className="input" placeholder="Longitude (106.xxxxx)" value={wForm.lng}
                  onChange={e => setWForm(f => ({ ...f, lng: e.target.value }))} />
              </div>
              <button type="button" className="btn btn-outline btn-sm btn-full" onClick={() => {
                if (!navigator.geolocation) { alert('GPS tidak tersedia'); return; }
                navigator.geolocation.getCurrentPosition(
                  pos => setWForm(f => ({ ...f, lat: pos.coords.latitude.toFixed(7), lng: pos.coords.longitude.toFixed(7) })),
                  () => alert('Gagal ambil GPS')
                );
              }}>
                📍 Ambil dari Lokasi Saat Ini
              </button>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Atau isi manual dari Google Maps (klik kanan → Salin koordinat)
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost btn-full" onClick={() => setShowWilayahForm(false)}>Batal</button>
              <button className="btn btn-gold btn-full" onClick={saveWilayah} disabled={saving}>{saving ? '...' : 'Simpan'}</button>
            </div>
          </div>
        </div>
      )}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 72, height: 72, borderRadius: 16, background: 'linear-gradient(135deg, var(--navy-500), var(--gold-glow))', border: '2px solid var(--border-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', fontWeight: 800, color: 'var(--gold-400)', margin: '0 auto 12px', overflow: 'hidden' }}>
                {selectedUser.foto_url ? <img src={selectedUser.foto_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} /> : selectedUser.name?.charAt(0).toUpperCase()}
              </div>
              <h2 style={{ marginBottom: 4 }}>{selectedUser.name}</h2>
              <span className={`badge ${selectedUser.role === "admin" ? "badge-red" : selectedUser.role === "spv" ? "badge-yellow" : selectedUser.role === "anggota" ? "badge-green" : "badge-blue"}`}>{getRoleLabel(selectedUser.role)}</span>
            </div>
            <div className="divider" />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Email</span>
                <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>{selectedUser.email}</span>
              </div>
              {selectedUser.phone && <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>No. HP</span>
                <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>{selectedUser.phone}</span>
              </div>}
              {selectedUser.wilayah_nama && <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Wilayah</span>
                <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>📍 {selectedUser.wilayah_nama}</span>
              </div>}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Status</span>
                <span className={selectedUser.is_active ? "badge badge-green" : "badge badge-gray"}>{selectedUser.is_active ? "Aktif" : "Nonaktif"}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button className="btn btn-ghost btn-full" onClick={() => setSelectedUser(null)}>Tutup</button>
              {selectedUser.id !== user.id && <button className="btn btn-danger btn-full" onClick={async () => {
                if (!window.confirm("Nonaktifkan " + selectedUser.name + "?")) return;
                try { await api.delete("/users/" + selectedUser.id); showMsg("User dinonaktifkan"); setSelectedUser(null); fetchAll(); }
                catch (err) { showMsg(err.message, "error"); }
              }}>Nonaktifkan</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
